<script>
import { ref, computed, watch } from 'vue'
import { useKeyboardNavigation } from '@/composables/useKeyboardNavigation.js'

export default {
  name: 'ShootingGalleryItemFullscreen',
  props: {
    images: {
      type: Array,
      required: true,
      default: () => []
    },
    currentIndex: {
      type: Number,
      required: true,
      default: 0
    }
  },
  setup(props, { emit }) {
    const activeIndex = ref(props.currentIndex)
    const isActive = computed(() => true)
    const maxIndex = computed(() => props.images.length - 1)

    // Update activeIndex when currentIndex prop changes
    watch(
      () => props.currentIndex,
      (newIndex) => {
        activeIndex.value = newIndex
      }
    )

    // Use keyboard navigation composable
    const {
      navigateLeft,
      navigateRight,
      close: closeFullscreen
    } = useKeyboardNavigation({
      currentIndex: activeIndex,
      maxIndex,
      isActive
    })

    // Watch for when activeIndex becomes null (from keyboard Escape)
    // and emit the close event to parent
    watch(activeIndex, (newValue) => {
      if (newValue === null) {
        emit('toggle-fullscreen', false, 0)
      }
    })

    const currentUrl = computed(() => {
      if (props.images[activeIndex.value]) {
        return props.images[activeIndex.value].url
      }
      return ''
    })

    const close = () => {
      closeFullscreen()
      emit('toggle-fullscreen', false, activeIndex.value)
    }

    const handlePrevious = () => {
      navigateLeft()
    }

    const handleNext = () => {
      navigateRight()
    }

    return {
      activeIndex,
      currentUrl,
      close,
      handlePrevious,
      handleNext
    }
  }
}
</script>

<template>
  <div>
    <div class="fullscreen-image">
      <v-btn fab small class="buttons mr-5" @click="handlePrevious">
        <v-icon>arrow_left</v-icon>
      </v-btn>

      <div class="fullscreen-image__inner">
        <img alt="fullscreen image shooting" crossorigin="anonymous" :src="currentUrl" />
        <div class="trigger-container" @click="close"></div>
      </div>

      <v-btn fab small class="buttons ml-5" @click="handleNext">
        <v-icon>arrow_right</v-icon>
      </v-btn>
    </div>

    <v-btn class="close-btn" fab small @click="close">
      <v-icon>close</v-icon>
    </v-btn>
  </div>
</template>

<style lang="less" scoped>
.fullscreen-image {
  position: fixed;
  z-index: 997;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
}

.fullscreen-image__inner {
  position: relative;
  width: 80%;
  height: 80%;
  overflow: hidden;
  z-index: 998;
  display: flex;
  justify-content: center;
  align-items: center;
}

.buttons {
  position: relative;
  z-index: 1000;
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .fullscreen-image img {
    transform: translate(-50%, -50%);
  }
}

.fullscreen-image img {
  position: absolute;
  max-width: 100%;
  max-height: 100%;
  z-index: 1000;
}

.trigger-container {
  position: fixed;
  top: 0;
  left: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  z-index: 999;
}

.close-btn {
  position: fixed;
  z-index: 1000;
  top: 25px;
  right: 25px;
}
</style>
